import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PackageName from '../PackageName';
import StepContainer from '../StepContainer';
import FixedFooter from '../Footer/FixedFooter';
import PackageDetailsModal from '../Modals/PackageDetailsModal';
import EditProviderModal from '../Modals/EditProviderModal';
import CreateFreeAccount from '../CreateFreeAccount';

import { useGetPreneedPackages } from '../../customHooks';
import { formatAddress } from '@evdy/web-core/dist/lib/utils';
import { renderToast } from '@evdy/web-core/dist/lib/utils';
import { formatPhoneNumber } from '@evdy/web-core/dist/lib/utils';
import { formatEven, getSelectedAddOnsPriceTotals } from '../../helperFns';

import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import checkGreyBackground from '../../public/images/icons/check-grey-back.svg';
import houseHeartGreyBackground from '../../public/images/icons/house-heart-grey-back.svg';
import freeImg from '../../public/images/free.svg';
import checkBlue from '../../public/images/icons/check-blue.svg';
import chevronRightWhite from '../../public/images/chevron-right-white.svg';
import handHoldingPhone from '../../public/images/hand-holding-phone.svg';

import styles from './YourPrice.module.scss';

const { saveLastScreen, updateAddOns, resetSelectedFuneralHome } = authUserOperations;

const {
  'your-price': yourPriceClass,
  hero: heroClass,
  'hero-desktop-inset': heroDesktopInsetClass,
  'section-title': sectionTitleClass,
  'key-elements': keyElementsClass,
  'key-elements-list': keyElementsListClass,
  'key-elements-list-item': keyElementsListItemClass,
  'key-elements-provider': keyElementsProvider,
  'key-elements-provider-details': providerDetailsClass,
  'key-elements-provider-name': providerNameClass,
  'key-elements-provider-addr': providerAddressClass,
  'planning-intro': planningIntroClass,
  'planning-intro-header': planningIntroHeaderClass,
  addons: addonsClass,
  addon: addOnClass,
  'hand-phone': handPhoneClass,
  'evdy-discount': evdyDiscountClass,
  'discount-title': discountTitleClass,
  'discount-description': discountDescription,
  'discount-description-item': discountDescriptionItem,
  'total-price': totalPriceClass,
  pricing: pricingClass,
  'outright-price': outrightPriceClass,
  'previous-price': previousPriceClass,
  'discount-price': discountPriceClass,
  'discounted-price': discountedPriceClass,
  'border-accent': borderAccentClass,
  'insurance-price': insurancePriceClass,
  'promises-section': promisesSection,
  'promise-copy': promiseCopyClass,
  promise: promiseClass,
  shield: shieldClass,
  title: titleClass,
  subtitle: subtitleClass,
  disclaimer: disclaimerClass
} = styles;

const YourPrice = ({ location }) => {
  const {
    authUser,
    preneedPackages = [],
    packageInfo = [],
    providers = [],
    stateWarning
  } = useSelector(({ authUser, webPurchase }) => ({
    authUser: authUser,
    preneedPackages: webPurchase.preneedPackages.data?.packages,
    packageInfo: webPurchase.preneedPackages.data?.packages,
    providers: webPurchase?.providers?.array,
    stateWarning: webPurchase?.providers?.stateWarning
  }));

  const dispatch = useDispatch();

  const [isPackageDetailsModalOpen, setPackageDetailsModalOpen] = useState(false);
  const [isFuneralHomeModalOpen, setFuneralHomeModalOpen] = useState(false);
  const [currentModalPackage, setCurrentModalPackage] = useState('');

  const { isFetching, data: userData, planPackageUpdated, funeralHomeSelected } = authUser || {};
  const {
    packageId: currentPackageId,
    funeralHome,
    estimates,
    userInfo,
    addOns = [],
    paymentOptions = []
  } = userData?.preneed || {};
  const { name: userName, phone } = userInfo || {};
  const { name: funeralHomeName, address: funeralHomeAddress } = funeralHome || {};

  // get preneed packages
  useGetPreneedPackages();

  const currentUserPackage = preneedPackages?.find(pkg => currentPackageId === pkg.packageId);
  const {
    name,
    reviewPackageName,
    description: packageDescription,
    discount,
    services,
    bulletPoints,
    colors,
    imageUrlTall,
    reviewPlanDescription = '',
    consumerServices
  } = currentUserPackage || {};
  const packagePaymentOptions = paymentOptions?.find(pkg => currentPackageId === pkg.packageId);
  // const packageEstimates = estimates?.find(pkg => currentPackageId === pkg.packageId);
  const { outright, insurance } = packagePaymentOptions || {};
  const { premium: outrightPrice } = outright ?? {};
  const { premium: insurancePrice, years: insuranceYears } = insurance ?? {};
  const homeNameString = funeralHome?.name || 'The funeral home';
  const packageHomeDescription = reviewPlanDescription.replace('<funeralHomeName>', homeNameString);
  const isPreferred = funeralHome?.packageSetupComplete;

  const discountFeatures = [
    '24/7 access & easy sharing',
    '5GB storage for added details',
    'Online memorials & tributes'
  ];

  const goToNextStep = () => {
    dispatch(saveLastScreen({ screen: 'submitOrder' }));
  };

  // show success toast after saving phone
  useEffect(() => {
    if (planPackageUpdated) {
      renderToast({
        id: 'save phone success',
        message: `Phone number ${formatPhoneNumber(phone)} saved to your account`
      });
    }
  }, [planPackageUpdated, phone]);

  // close provider modal on success
  useEffect(() => {
    if (funeralHomeSelected) {
      setFuneralHomeModalOpen(false);
      dispatch(resetSelectedFuneralHome());
    }
  }, [dispatch, funeralHomeSelected]);

  const handleClickAddon = addOn => {
    const selectedAddonIdx = addOns.findIndex(a => a.serviceId === addOn.serviceId);
    const updatedAddon = {
      ...addOns[selectedAddonIdx],
      selected: !addOns[selectedAddonIdx].selected
    };

    const updatedAddOns = [
      ...addOns.slice(0, selectedAddonIdx),
      updatedAddon,
      ...addOns.slice(selectedAddonIdx + 1, addOns.length)
    ];

    dispatch(updateAddOns({ updatedAddOns: updatedAddOns }));
  };

  // these are used if price totals are calculated on client-side/FE
  // const selectedAddOnsPriceTotals = getSelectedAddOnsPriceTotals(addOns);
  // const calculatedOutrightPrice = outrightPrice + selectedAddOnsPriceTotals.outright;
  // const calculatedMonthlyPrice = insurancePrice + selectedAddOnsPriceTotals.monthly;

  const slashedPrice = outrightPrice + Number(discount);

  return (
    <>
      <StepContainer
        evelynArray={[
          { content: 'Let’s review your plan.', style: 'header' },
          'Congratulations on completing your plan! We’ll email you a personal plan overview to print and share with your family so they have the answers they’ll need.'
        ]}
        showLoader={isFetching}
        {...{ location }}
      >
        <div className={yourPriceClass}>
          <div className={heroClass}>
            <PackageName
              decedentName={`${userName?.first} ${userName?.last}`}
              page="price"
              name={`${reviewPackageName} Plan`}
            />
            <p>{packageHomeDescription}</p>
          </div>
          <div className={`${sectionTitleClass} ${heroDesktopInsetClass}`}>
            key elements of your plan:
          </div>
          <div className={keyElementsClass}>
            <div className={keyElementsListClass}>
              {bulletPoints?.map(name => (
                <div key={name} className={keyElementsListItemClass}>
                  <img src={checkGreyBackground} alt="" />
                  {name}
                </div>
              ))}
            </div>
            <div className={keyElementsProvider}>
              <img src={houseHeartGreyBackground} alt="" />
              <div className={providerDetailsClass}>
                <h1>Funeral Home:</h1>
                <div className={providerNameClass}>{funeralHomeName}</div>
                <div className={providerAddressClass}>{formatAddress(funeralHomeAddress)}</div>
                <button onClick={() => setFuneralHomeModalOpen(true)}>Change funeral home</button>
              </div>
            </div>
          </div>
          {!phone && <CreateFreeAccount />}
          <div className={planningIntroClass}>
            <div className={planningIntroHeaderClass}>
              <h1>Secure Your Plan</h1>
              <button
                onClick={() => {
                  setPackageDetailsModalOpen(true);
                  setCurrentModalPackage({
                    name,
                    colors,
                    image: imageUrlTall,
                    services,
                    description: packageDescription
                  });
                }}
              >
                View Full Package Details
              </button>
            </div>
            <p>
              {isPreferred
                ? 'Taking care of the costs saves loved ones from the burden of unforeseen expenses in the future.'
                : 'Taking care of the costs saves loved ones from the burden of unforeseen expenses in the future. Once you submit your order request, we’ll contact the home of your choice to get their exact pricing and agreement to deliver this package.'}
            </p>
          </div>
          <div className={sectionTitleClass}>Important items to consider adding:</div>
          <div className={addonsClass}>
            {addOns.map(addOn => (
              <div key={addOn.serviceId} className={addOnClass}>
                <div onClick={() => handleClickAddon(addOn)}>
                  <img src={addOn.image} alt="add on" />
                  <input checked={addOn.selected} type="radio" />
                  <p>
                    <span>{addOn.name}</span>
                    <span>(${addOn.price})</span>
                  </p>
                </div>
                <p>{addOn.description}</p>
              </div>
            ))}
          </div>
          <div className={evdyDiscountClass}>
            <img className={handPhoneClass} src={handHoldingPhone} alt="" />
            <div className={discountTitleClass}>
              <input checked type="radio" />
              <div>
                <h1>Everdays digital features</h1>
                <p>(${discount})</p>
                <div>
                  <img src={freeImg} alt="" />
                  <p>Limited Time Only</p>
                </div>
              </div>
            </div>
            <div className={discountDescription}>
              {discountFeatures.map(feature => (
                <div key={feature} className={discountDescriptionItem}>
                  <img src={checkBlue} alt="" />
                  <p>{feature}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={totalPriceClass}>
            <h3>Your {isPreferred ? '' : 'estimated '}all-inclusive price</h3>
            <p>
              At Everdays we believe in transparency so you and your family never have any hidden
              fees.
            </p>
            <div className={pricingClass}>
              <div>
                <div className={outrightPriceClass}>
                  <div>
                    <div className={previousPriceClass}>{formatEven(slashedPrice)}</div>
                    <div className={discountPriceClass}>${discount} off!</div>
                  </div>
                  <div>
                    <div className={discountedPriceClass}>{formatEven(outrightPrice)}</div>
                  </div>
                </div>
                <div className={borderAccentClass} />
                <div className={insurancePriceClass}>
                  <p>
                    <strong>or {formatEven(insurancePrice)}</strong>/mo ({insuranceYears * 12} mos)
                  </p>
                  <p>Coverage begins on day one.*</p>
                </div>
              </div>
              <button onClick={goToNextStep}>
                Get My Plan
                <img src={chevronRightWhite} alt="" />
              </button>
            </div>
          </div>
          <div className={promisesSection}>
            <div className={promiseClass}>
              <div className={shieldClass} />
              <div className={promiseCopyClass}>
                <p className={titleClass}>No Buyers Remorse with Everdays</p>
                <p className={subtitleClass}>
                  We know circumstances change and life happens – with us it’s never a problem. You
                  can cancel for a full refund within 30 days of purchase, no questions asked.
                </p>
              </div>
            </div>
            <div className={promiseClass}>
              <div className={shieldClass} />
              <div className={promiseCopyClass}>
                <p className={titleClass}>Funds Secured & Insured</p>
                <p className={subtitleClass}>
                  Your payments are held by 100-year-old, A+ rated insurance company Homesteaders
                  Life to ensure full fund and package delivery when the time comes.
                </p>
              </div>
            </div>
            <div className={promiseClass}>
              <div className={shieldClass} />
              <div className={promiseCopyClass}>
                <p className={titleClass}>Relocate Anywhere</p>
                <p className={subtitleClass}>
                  If you change your mind, move somewhere else, or something happens to the provider
                  you selected, our care team will be here to help you switch to a different funeral
                  home.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className={disclaimerClass}>
          *If you choose our insurance plan, full coverage goes into effect no later than 24 months
          from your first payment. Until then, your family is protected but at a lesser amount.
        </div>
      </StepContainer>
      <FixedFooter
        backCopy="Back"
        backAction={() => {
          dispatch(saveLastScreen({ screen: 'chooseProvider' }));
        }}
        isValid={true}
        nextCopy="Get My Plan"
        nextAction={goToNextStep}
      />
      <PackageDetailsModal
        {...currentModalPackage}
        addOns={consumerServices?.filter(({ addOn }) => !!addOn)}
        modalVisible={isPackageDetailsModalOpen}
        closeModal={() => setPackageDetailsModalOpen(false)}
      />
      <EditProviderModal
        closeModal={() => setFuneralHomeModalOpen(false)}
        modalVisible={isFuneralHomeModalOpen}
        {...{ userData, providers, packageInfo, stateWarning }}
      />
    </>
  );
};

export default YourPrice;
